import { useState } from "react";
import { toast } from "sonner";
import {
  Lock,
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ForcedPasswordChangeModal
 * 
 * Displayed when a user logs in with a temporary password and must change it.
 * Works for both contractors and suppliers.
 * 
 * Props:
 * - open: boolean - whether modal is visible
 * - token: string - JWT token from login
 * - userType: "contractor" | "supplier"
 * - onSuccess: (newToken: string) => void - called with new token after password change
 * - userName: string - display name
 */
export default function ForcedPasswordChangeModal({ 
  open, 
  token, 
  userType = "contractor",
  onSuccess,
  userName = "there"
}) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const endpoint = userType === "contractor" 
        ? `${API_URL}/api/auth/contractor/change-password`
        : `${API_URL}/api/auth/supplier/change-password`;

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          new_password: newPassword,
          confirm_password: confirmPassword
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to change password");
      }

      const data = await res.json();
      toast.success("Password changed successfully!");
      
      // Call success callback with new token
      if (onSuccess && data.token) {
        onSuccess(data.token);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getAccentColor = () => {
    return userType === "contractor" ? "emerald" : "cyan";
  };

  const color = getAccentColor();

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className={`w-14 h-14 bg-${color}-100 rounded-full flex items-center justify-center mx-auto mb-4`}>
            <ShieldCheck className={`w-7 h-7 text-${color}-600`} />
          </div>
          <DialogTitle className="text-center text-xl">
            Change Your Password
          </DialogTitle>
          <DialogDescription className="text-center">
            Hello {userName}! For security, please set a new password to continue.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            You&apos;re using a temporary password. You must change it before you can access your dashboard.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label className="text-slate-700">New Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                className="pl-10 pr-10"
                required
                minLength={8}
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-slate-700">Confirm New Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                className="pl-10"
                required
                data-testid="confirm-password-input"
              />
            </div>
          </div>

          <div className="text-xs text-slate-500 space-y-1">
            <p>Password requirements:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li className={newPassword.length >= 8 ? "text-green-600" : ""}>
                At least 8 characters
              </li>
              <li className={newPassword && newPassword === confirmPassword ? "text-green-600" : ""}>
                Passwords match
              </li>
            </ul>
          </div>

          <Button
            type="submit"
            disabled={loading || newPassword.length < 8 || newPassword !== confirmPassword}
            className={`w-full bg-${color}-600 hover:bg-${color}-700 text-white py-5`}
            data-testid="change-password-submit"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Changing Password...
              </>
            ) : (
              <>
                <ShieldCheck className="w-4 h-4 mr-2" />
                Set New Password
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
